import { useEffect, useState } from 'react';

import './PublicKnowledgeSiteSetting.css';
import { useRecoilValue } from 'recoil';
import Utils from '@/common/utils/utils';
import CropImage from '@/component/common/form/crop-image/index';
import { corporationAtom } from '@/state/atom/corporationAtom';
import Constant from '@/common/constant/Constant';
import Storage from '@/amplify/storage';
import { IconButton } from '@material-ui/core';
import trI18n from '@/common/utils/trI18n';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';

const SettingLogoPublicSite = props => {
    // Re-render when the translation dictionary loads/extends so tr() text updates.
    useTranslationSubscription();

    const corporationDetail = useRecoilValue(corporationAtom);
    const { setCroppedImage, errors, setErrors, settingData, setSettingData, setIsChangeData, reloadPage } = props;

    const [inputImage, setInputImage] = useState('');
    const [openCrop, setOpenCrop] = useState(false);
    const [imagePreview, setImagePreview] = useState('');
    const [logoFileName, setLogoFileName] = useState('');
    const [errorImage, setErrorImage] = useState('');
    const cropShape = '';
    const aspect = 3 / 1;

    useEffect(() => {
        initLogoData();
    }, [settingData.logo, reloadPage]);

    useEffect(() => {
        let errorsClone = { ...errors };
        if (errorImage) {
            errorsClone.logo = errorImage;
        } else {
            delete errorsClone.logo;
        }

        setErrors(errorsClone);
    }, [errorImage]);

    const initLogoData = async () => {
        setErrorImage('');
        setCroppedImage(null);

        if (settingData.logo) {
            setLogoFileName(
                Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, corporationDetail.name)
            );
            // get logo preview
            let logo = await Storage.get(settingData.logo);
            setImagePreview(logo);
        } else {
            setImagePreview('');
            setLogoFileName('');
        }
    };

    const onLogoFileChange = event => {
        setIsChangeData(true);
        let file = event.target.files[0];

        if (file) {
            setLogoFileName(file.name);
        } else {
            setLogoFileName('');
            setImagePreview('');
            return;
        }

        if (!validateUpdateImage(file)) return;

        if (file) {
            setLogoFileName(file.name);
            Utils.readFileAsArrayBuffer(file).then(reader => {
                const buffer = new Buffer(reader.result);
                const imgBase64 = buffer.toString('base64');
                setInputImage(`data:${file.type};base64,${imgBase64}`);
                setOpenCrop(true);
                event.target.value = '';
            });
        }
    };

    const onCloseCropEditor = () => {
        setOpenCrop(false);
        if (settingData.logo) {
            setLogoFileName(
                Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, corporationDetail.name)
            );
        } else {
            setImagePreview('');
            setLogoFileName('');
            setErrorImage('');
            setCroppedImage(null);
        }
    }

    const onRemoveLogo = () => {
        setIsChangeData(true);
        setImagePreview('');
        setLogoFileName('');
        setErrorImage('');
        setCroppedImage(null);

        setSettingData(prev => {
            prev.logo = '';
            return prev;
        });
    };

    const validateUpdateImage = file => {
        if (!file || !file?.size) return false;

        let imageExtension = file.name
            ?.split('.')
            ?.pop()
            ?.toLowerCase();
        const acceptedFileExtension = [
            'tiff',
            'tif',
            'jpeg',
            'jpg',
            'jpe',
            'jfif',
            'jfi',
            'jif',
            'bmp',
            'gif',
            'png'
        ];
        if (!acceptedFileExtension.includes(imageExtension)) {
            setErrorImage('USER01MSG007');
            return false;
        }

        let sizeMB = Utils.convertByteToMB(file.size)
        if (sizeMB > 5) {
            setErrorImage(trI18n('COMMSG035').replace('{{fileSize}}', 5));
            return false;
        }

        return true;
    };

    return (
        <div className="embeddedCode">
            <div className="embeddedCode-label">{trI18n('PLKNOWLB008')}</div>
            <div className="logo-wrap">
                <div className="logo-image">
                    {logoFileName && (
                        <div>
                            <IconButton onClick={onRemoveLogo} className="itemDelete">
                                <img src="/assets/image/icon-closeUpfile.svg" alt="" />
                            </IconButton>
                        </div>
                    )}
                    <div className="image-preview">
                        <img src={imagePreview} alt="" />
                    </div>
                </div>
                <div className="logo-name">
                    {logoFileName && (
                        <div className="logo-filename">
                            <span>{logoFileName}</span>
                        </div>
                    )}
                    <input
                        onChange={onLogoFileChange}
                        accept="image/png, image/gif, image/jpeg, image/tiff, image/bmp"
                        className="updateFile"
                        id="public-kn-site-setting-logo"
                        type="file"
                    />
                    <label htmlFor="public-kn-site-setting-logo">
                        <div className="upload-logo">{tr('画像ファイルを選択')}</div>
                    </label>

                    {errorImage && <div className="imageError">{trI18n(errorImage)}</div>}
                </div>

                <CropImage
                    openCrop={openCrop}
                    setOpenCrop={setOpenCrop}
                    onCloseCallback={onCloseCropEditor}
                    aspect={aspect}
                    cropShape={cropShape}
                    inputImage={inputImage}
                    setInputImage={setInputImage}
                    setCroppedImage={props.setCroppedImage}
                    setErrorImage={setErrorImage}
                    setImagePreview={setImagePreview}
                />
            </div>
        </div>
    );
};

export default SettingLogoPublicSite;
